"use client";

import type { SurfaceStyle } from "@/cms/appearance";
import { surfaceStyle } from "@/utils/visual-style";
import { glassSearchSurface, resolveSurface } from "@/utils/visual-defaults";
import { useId } from "react";
import { useRouter } from "next/navigation";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-light-svg-icons";
import { twMerge } from "tailwind-merge";
import RippleCtaButton from "./RippleCtaButton";
import { searchTarget, type SearchSite } from "./search-target";

interface SearchBarComponentProps {
  config?: {
    cmsData: SearchSite;
    variant?: "glass";
    surface?: SurfaceStyle;
    bgColor?: string;
    input?: {
      placeholderText?: string;
      placeholderTextColor?: string;
      bgColor?: string;
      textColor?: string;
      borderColor?: string;
      focusBorderColor?: string;
    };
    button?: {
      text?: string;
      bgColor?: string;
      textColor?: string;
      hoverBgColor?: string;
      hoverTextColor?: string;
    };
  };
  toggleSearch?: () => void;
}

const SearchBarComponent: React.FC<SearchBarComponentProps> = ({
  config,
  toggleSearch,
}) => {
  const inputId = useId();
  const { cmsData } = config || {};

  const router = useRouter();
  const [formData, setFormData] = React.useState({ q: "" });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    toggleSearch?.();

    const query = formData.q.trim() ?? "";
    setFormData({ q: "" });

    const defaultSearchPage = searchTarget(cmsData, location?.pathname);

    if (formData.q.trim()) {
      router.push(`${defaultSearchPage}?q=${encodeURIComponent(query)}`);
    } else {
      router.push(defaultSearchPage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (config?.variant === "glass")
    return (
      <form
        role="search"
        onSubmit={handleSubmit}
        className="re-search re-surface"
        style={
          {
            ...surfaceStyle(
              resolveSurface(glassSearchSurface, config.surface),
              config.bgColor,
            ),
            "--re-search-button-bg": config.button?.bgColor,
            "--re-search-button-color": config.button?.textColor,
            "--re-search-hover-opacity": `${Math.min(1, (config.surface?.backgroundOpacity ?? glassSearchSurface.backgroundOpacity) + 0.1) * 100}%`,
            "--re-search-focus-opacity": `${Math.min(1, (config.surface?.backgroundOpacity ?? glassSearchSurface.backgroundOpacity) + 0.2) * 100}%`,
            "--re-search-focus-border-opacity": `${Math.min(1, (config.surface?.borderOpacity ?? glassSearchSurface.borderOpacity) + 0.2) * 100}%`,
          } as React.CSSProperties
        }
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} className="re-search-icon" />
        <label className="sr-only" htmlFor={inputId}>
          Søg efter bolig
        </label>
        <input
          id={inputId}
          name="q"
          value={formData.q}
          onChange={handleChange}
          placeholder={config.input?.placeholderText ?? "Søg efter bolig"}
        />
        <button type="submit" className="re-search-button">
          {config.button?.text ?? "Søg"}
        </button>
      </form>
    );

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      style={
        {
          "--bg-color": config?.bgColor ?? "white",
        } as React.CSSProperties
      }
      className={twMerge("w-full p-2 flex", "bg-[var(--bg-color)]")}
    >
      <input
        type="text"
        id="q"
        name="q"
        style={
          {
            "--text-color":
              config?.input?.textColor ?? "var(--input-text-color)",
            "--bg-color": config?.input?.bgColor ?? "var(--input-bg-color)",
            "--border-color": config?.input?.borderColor ?? "",
            "--focus-border-color":
              config?.input?.focusBorderColor ?? "var(--input-text-color)",
            "--placeholder-text-color":
              config?.input?.placeholderTextColor ?? "text-gray-800",
          } as React.CSSProperties
        }
        className={twMerge(
          config?.input?.borderColor ? "border" : "",
          "p-2 w-full text-gray-800 focus:outline-none bg-[var(--bg-color)] text-[var(--text-color)] border-[var(--border-color)] focus:border-[var(--focus-border-color)] placeholder-[var(--placeholder-text-color)]",
          "placeholder:bg-green-[var(--placeholder-text-color)]",
        )}
        placeholder={
          config?.input?.placeholderText ??
          "Søg på sagsnr., by vej, postnr eller kommune"
        }
        value={formData.q}
        onChange={handleChange}
      />
      <RippleCtaButton
        type="submit"
        config={{
          bgColor: config?.button?.bgColor ?? "var(--search-button-bg-color)",
          textColor:
            config?.button?.textColor ?? "var(--search-button-text-color)",
          hoverBgColor:
            config?.button?.hoverBgColor ??
            "var(--search-button-hover-bg-color)",
          hoverTextColor:
            config?.button?.hoverTextColor ??
            "var(--search-button-hover-text-color)",
        }}
      >
        <span className="flex items-center gap-2">
          <FontAwesomeIcon className="size-[24px]" icon={faMagnifyingGlass} />
          {config?.button?.text || "Søg"}
        </span>
      </RippleCtaButton>
    </form>
  );
};

export default SearchBarComponent;
